import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { queryKeys } from "@/lib/query-keys";
import type { Tables } from "@/integrations/supabase/types";
export type Notification = Tables<"user_notifications">;

/**
 * The unread badge count, and nothing else.
 *
 * `NotificationBell` mounts in the header on every page. It used to call
 * `useNotifications`, which pulls 50 full rows on mount — a second copy of the
 * same fetch the dropdown performs — just to render a number. This is a `HEAD`
 * count query, so the badge costs one integer.
 */
export const useUnreadNotificationCount = (userId: string | null) => {
  const { data, isLoading } = useQuery<number>({
    queryKey: queryKeys.userNotifications.unreadCount(userId),
    enabled: !!userId,
    queryFn: async (): Promise<number> => {
      // `user_notifications` is missing from the generated types; cast as the
      // rest of this hook already does implicitly.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { count, error } = await (db as any).from("user_notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId as string)
        .eq("is_read", false);

      if (error) throw error;
      return (count as number | null) ?? 0;
    },
    staleTime: 60_000,
    // There is no realtime channel on this table (the app talks to the REST
    // gateway, not supabase-js), so returning to the tab is the only moment the
    // badge gets to notice a notification that arrived while it was hidden.
    refetchOnWindowFocus: true,
  });

  return { unreadCount: data ?? 0, isLoading };
};

export const useNotifications = (
  userId: string | null,
  options: { enabled?: boolean } = {},
) => {
  // The dropdown passes `enabled: open`, so the 50-row list is fetched when the
  // menu opens instead of on every page mount.
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = useCallback(async () => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await db.from("user_notifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      const typedNotifications = (data || []) as Notification[];
      setNotifications(typedNotifications);
      setUnreadCount(typedNotifications.filter(n => !n.is_read).length);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!userId) return { success: false, error: "Not authenticated" };

    try {
      const { error } = await db.from("user_notifications")
        .update({ is_read: true })
        .eq("id", notificationId)
        .eq("user_id", userId);

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      // Immediately decrement unread count for instant badge update
      setUnreadCount(prev => Math.max(0, prev - 1));
      // The bell reads its own count query; keep it in step.
      void queryClient.invalidateQueries({ queryKey: queryKeys.userNotifications.all });

      return { success: true };
    } catch (error) {
      console.error("Error marking notification as read:", error);
      return { success: false, error };
    }
  }, [userId, queryClient]);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return { success: false, error: "Not authenticated" };

    try {
      const { error } = await db.from("user_notifications")
        .update({ is_read: true })
        .eq("user_id", userId)
        .eq("is_read", false);

      if (error) throw error;

      // Update local state immediately for instant UI update
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      // Immediately set unread count to 0 for instant badge update
      setUnreadCount(0);
      // Reload notifications to ensure all components stay in sync with database
      await loadNotifications();
      void queryClient.invalidateQueries({ queryKey: queryKeys.userNotifications.all });

      return { success: true };
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      return { success: false, error };
    }
  }, [userId, loadNotifications, queryClient]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!userId) return { success: false, error: "Not authenticated" };

    try {
      const notificationToDelete = notifications.find(n => n.id === notificationId);
      const wasUnread = notificationToDelete && !notificationToDelete.is_read;

      const { error } = await db.from("user_notifications")
        .delete()
        .eq("id", notificationId)
        .eq("user_id", userId);

      if (error) throw error;

      // Update local state immediately
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Decrement unread count if the deleted notification was unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.userNotifications.all });

      return { success: true };
    } catch (error) {
      console.error("Error deleting notification:", error);
      return { success: false, error };
    }
  }, [userId, notifications, queryClient]);

  // Recalculate unread count whenever notifications change
  // This ensures the badge always shows the correct number
  useEffect(() => {
    const unread = notifications.filter(n => !n.is_read).length;
    setUnreadCount(unread);
  }, [notifications]);

  // Load notifications when enabled (the dropdown only when it opens) and when
  // the user changes.
  useEffect(() => {
    if (!enabled) return;
    loadNotifications();
  }, [loadNotifications, enabled]);

  return {
    notifications,
    loading,
    unreadCount,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
};

